import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { IdPropType } from '../../../proptypes/basic';
import NestedResourceTable from '../../table/components/resource/NestedResourceTable';
import {
    CARE_NEED_RESOURCE,
    CONTRACT_RESOURCE,
    NURSE_DEPLOYMENT_RESOURCE,
    NURSE_RESOURCE,
    NURSE_TRANSFER_RESOURCE,
} from '../../api/resources';
import CareNeed from '../../careNeeds/components/CareNeed';
import NurseDeploymentType from './NurseDeploymentType';
import AgencyLink from '../../agencies/components/AgencyLink';
import { useResourceDialog } from '../../dialogs/hooks';
import TransferDate from '../../nurseTransfers/components/TransferDate';
import { TRANSFER_TYPE_ARRIVAL, TRANSFER_TYPE_DEPARTURE } from '../../nurseTransfers/types';
import NurseDeploymentDetails from './NurseDeploymentDetails';
import A1State from '../../a1document/components/A1State';
import { NURSE_DEPLOYMENT_TYPE_NURSE } from '../types';
import TransferDialogButton from '../../nurseTransfers/components/TransferDialogButton';
import NurseName from '../../nurses/components/NurseName';
import { useDialog } from '../../dialogs/components/DialogContext';
import { useCareNeedsByCustomerId } from '../../careNeeds/careNeedsSlice';
import { useSnackbar } from '../../snackbar/hooks';
import { useContractsByCustomerId } from '../../contracts/contractsSlice';
import { isActiveAgencyContract } from '../../contracts/hooks';
import { selectAllAgencies } from '../../agencies/agenciesSlice';
import CareNeedWithoutAgencyDialog from './CareNeedWithoutAgencyDialog';

const initialOrderBy = [
    ['careTill', 'asc'],
    ['careFrom', 'desc'],
];
const nestedInitialOrderBy = [
    ['careFrom', 'desc'],
    ['careTill', 'desc'],
];

const columns = [
    { key: 'type', label: 'Typ', component: NurseDeploymentType },
    {
        key: 'agencyId',
        label: 'Agentur',
        component: ({ agencyId }) => {
            return agencyId ? <AgencyLink agencyId={agencyId} /> : null;
        },
    },
    {
        key: 'nurseId',
        label: 'Name',
        component: ({ nurseId, data: { type } }) =>
            type === NURSE_DEPLOYMENT_TYPE_NURSE && <NurseName nurseId={nurseId} />,
    },
    {
        key: 'careFrom',
        label: 'Datum von',
        component: ({ careFrom, data: { id } }) => (
            <TransferDate deploymentId={id} fallback={careFrom} type={TRANSFER_TYPE_ARRIVAL} />
        ),
    },
    {
        key: 'careTill',
        label: 'Datum bis',
        component: ({ careTill, data: { id } }) => (
            <TransferDate deploymentId={id} fallback={careTill} type={TRANSFER_TYPE_DEPARTURE} />
        ),
    },
    {
        key: 'a1State',
        accessor: 'id',
        as: 'nurseDeploymentId',
        label: 'A1',
        component: A1State,
        sortable: false,
    },
    {
        key: 'arrival',
        label: 'Reisedetails An',
        component: ({ data: { id: deploymentId, customerId, type, withoutArrTransfer } }) =>
            type === NURSE_DEPLOYMENT_TYPE_NURSE && !withoutArrTransfer ? (
                <TransferDialogButton
                    customerId={customerId}
                    deploymentId={deploymentId}
                    type={TRANSFER_TYPE_ARRIVAL}
                    createDocument
                />
            ) : null,
        sortable: false,
    },
    {
        key: 'departure',
        label: 'Reisedetails Ab',
        component: ({ data: { id: deploymentId, customerId, type, withoutDepTransfer } }) =>
            type === NURSE_DEPLOYMENT_TYPE_NURSE && !withoutDepTransfer ? (
                <TransferDialogButton
                    customerId={customerId}
                    deploymentId={deploymentId}
                    type={TRANSFER_TYPE_DEPARTURE}
                    createDocument
                />
            ) : null,
        sortable: false,
    },
];

const NurseDeployments = ({ customerId }) => {
    const listId = `customer.${customerId}.careNeeds`;
    const { openNurseDeploymentDialog } = useDialog();
    const { enqueueSnackbar } = useSnackbar();

    const careNeeds = useCareNeedsByCustomerId(customerId);
    //console.log('NurseDeployments careNeeds', careNeeds);

    const handleCareNeed = useResourceDialog(CARE_NEED_RESOURCE, { customerId });

    const agencies = useSelector(selectAllAgencies);
    const [contracts, { initialized: contractsInitialized }] = useContractsByCustomerId(customerId);
    // BK-Bedarf for which "+ Einsatz" was clicked although it has no agency yet
    const [careNeedWithoutAgencyId, setCareNeedWithoutAgencyId] = useState(null);

    const handleCreateNurseDeployment = useCallback(
        (careNeedId, extra) => {
            //console.log('handleCreateNurseDeployment', { careNeedId, extra,careNeeds });

            // check if care_need is still valid (without careTill date in the past)
            let care_need_closed= false;
            careNeeds[0].forEach((careNeed) => {
                //console.log('Checking careNeed', careNeed, "careNeedId", careNeedId);

                if (careNeed.careTill === null)
                    return;

                const today = new Date();
                const careTillDate = new Date(careNeed.careTill);

                if (careNeed.id === careNeedId) {
                    if (careTillDate !== null && careTillDate <= today) {
                        care_need_closed = true;
                    }
                }
            });
            if (care_need_closed) {
                let errorMessage = 'Dieser BK-Bedarf ist geschlossen, Sie können keine neuen Einsätze mehr hinzufügen!';
                enqueueSnackbar(
                    errorMessage,
                    {
                        key: 'multiple-care-needs',
                        variant: 'error',
                        persist: false,
                        style: { whiteSpace: 'pre-line' },
                        preventDuplicate: false
                    }
                );
                return;
            }

            // no BK-Einsatz without an agency and an active agency contract of that agency
            // (docs/plans/care-need-single-agency.md, rule 4) - checked here already so the
            // dialog doesn't open just to say so; the dialog itself checks again
            const careNeed = (careNeeds[0] || []).find((item) => item.id === careNeedId);
            const agencyId = (careNeed && careNeed.agencyId) || null;
            if (!agencyId) {
                setCareNeedWithoutAgencyId(careNeedId);
                return;
            }
            if (contractsInitialized) {
                const hasActiveContract = (contracts || []).some(
                    (contract) => isActiveAgencyContract(contract) && contract.agencyId === agencyId
                );
                if (!hasActiveContract) {
                    const agency = agencies.find((a) => a.id === agencyId);
                    enqueueSnackbar(
                        `Kein vollständiger, laufender Vertrag mit ${
                            agency ? agency.matchCode || agency.name : 'der Agentur'
                        } vorhanden – BK-Einsatz kann nicht angelegt werden.`,
                        {
                            key: 'no-active-contract',
                            variant: 'error',
                            persist: false,
                            preventDuplicate: true,
                        }
                    );
                    return;
                }
            }

            openNurseDeploymentDialog({ customerId, careNeedId, ...extra });
        },
        [
            openNurseDeploymentDialog,
            customerId,
            careNeeds,
            enqueueSnackbar,
            contractsInitialized,
            contracts,
            agencies,
        ]
    );

    const handleEditNurseDeployment = useResourceDialog(NURSE_DEPLOYMENT_RESOURCE, { customerId });

    /*
    const handleNestedCopy = useResourceDialog(NURSE_DEPLOYMENT_RESOURCE, {
        customerId,
        copy: true,
    });

    const extraActions = useMemo(
        () => [
            {
                key: 'copy',
                action: ({ data }) => (
                    <CopyResourceButton
                        onClick={(extra) => handleNestedCopy(data.id, extra)}
                        listId={`${listId}.${data.careNeedId}.${NURSE_DEPLOYMENT_RESOURCE}`}
                        resource={NURSE_DEPLOYMENT_RESOURCE}
                    />
                ),
                compact: ({ data }) => (
                    <CopyResourceIconButton
                        onClick={(extra) => handleNestedCopy(data.id, extra)}
                        size="small"
                        listId={`${listId}.${data.careNeedId}.${NURSE_DEPLOYMENT_RESOURCE}`}
                        resource={NURSE_DEPLOYMENT_RESOURCE}
                    />
                ),
            },
        ],
        [handleNestedCopy, listId]
     );
     */

    const fetchParams = useMemo(() => ({ customerId }), [customerId]);

    return (
        <>
            <NestedResourceTable
                label={({ dataId }) => <CareNeed careNeedId={dataId} />}
                listId={listId}
                resource={CARE_NEED_RESOURCE}
                fetchParams={fetchParams}
                orderBy={initialOrderBy}
                nestedColumns={columns}
                nestedResource={NURSE_DEPLOYMENT_RESOURCE}
                autoload
                createLabel="BK-Bedarf"
                onCreate={handleCareNeed}
                canCreate={false}
                onEdit={handleCareNeed}
                nestedLimit={5}
                onNestedCreate={handleCreateNurseDeployment}
                onNestedEdit={handleEditNurseDeployment}
                onNestedDelete={null}
                nestedWith={{
                    customerContract: CONTRACT_RESOURCE,
                    nurse: NURSE_RESOURCE,
                    arrivalTransfer: NURSE_TRANSFER_RESOURCE,
                    departureTransfer: NURSE_TRANSFER_RESOURCE,
                }}
                nestedInitialOrderBy={nestedInitialOrderBy}
                nestedExpand={NurseDeploymentDetails}
                nestedVariant="collapse"
            />
            <CareNeedWithoutAgencyDialog
                open={careNeedWithoutAgencyId !== null}
                onClose={() => setCareNeedWithoutAgencyId(null)}
                customerId={customerId}
                careNeedId={careNeedWithoutAgencyId}
            />
        </>
    );
};

NurseDeployments.propTypes = {
    customerId: IdPropType.isRequired,
};

NurseDeployments.defaultProps = {};

export default NurseDeployments;
